import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { useAccountContext } from './AccountContext';
import AvailableCredit from './detail/AvailableCredit';
import BillingDate from '../eod/BillingDate';
import CurrentBillingPeriod from './CurrentBillingPeriod';
import EodProgress from '../eod/EodProgress';
import { useAccountById, useCurrentBillingPeriod, useGetBillingPeriod } from '@app/sdk';
import { useAccountIdContext } from './AccountIdContext';
import { Error, CopyToClipboardIcon } from '@app/shared';
import { endOfDay, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { Segment } from 'semantic-ui-react';

export default function Account() {
  const { accountId } = useParams();
  const { setAccountId, setBillingDate, setIsBillingDateOutBound } = useAccountIdContext();
  const { error } = useAccountContext();
  const { data: account } = useAccountById(accountId);

  useEffect(() => {
    if (accountId) {
      setAccountId(accountId);
    }
  }, [accountId, setAccountId]);

  const { selectedBillingPeriod, setSelectedBillingPeriod, setPreviousBillingPeriod } = useAccountContext();
  const { data: currentBillingPeriod } = useCurrentBillingPeriod(accountId);
  useEffect(() => {
    if (selectedBillingPeriod == undefined && currentBillingPeriod != undefined) {
      setSelectedBillingPeriod(currentBillingPeriod);
    }
  }, [currentBillingPeriod, selectedBillingPeriod, setSelectedBillingPeriod]);

  const [previousBillingPeriodId, setPreviousBillingPeriodId] = useState<number | undefined>();
  const { data: previousBillingPeriod } = useGetBillingPeriod(accountId, previousBillingPeriodId);

  useEffect(() => {
    if (previousBillingPeriod) {
      setPreviousBillingPeriod(previousBillingPeriod);
      return;
    }
    setPreviousBillingPeriod(null);
  }, [previousBillingPeriod, setPreviousBillingPeriod]);

  useEffect(() => {
    if (currentBillingPeriod && currentBillingPeriod.billingPeriodId > 1) {
      setPreviousBillingPeriodId(currentBillingPeriod.billingPeriodId - 1);
    }
  }, [currentBillingPeriod]);

  useEffect(() => {
    if (accountId && currentBillingPeriod) {
      const loadedBillingDate = localStorage.getItem(`${accountId}-billing-date`);
      if (loadedBillingDate) {
        const date = new Date(loadedBillingDate);
        if (
          (currentBillingPeriod && endOfDay(date) < endOfDay(new Date(currentBillingPeriod.startDate))) ||
          endOfDay(date) > endOfDay(new Date(currentBillingPeriod.endDate))
        ) {
          toast.error('Billing date is not in the current billing period.');
          setIsBillingDateOutBound(true);
        } else {
          setIsBillingDateOutBound(false);
        }
        setBillingDate(parseISO(loadedBillingDate));
      } else {
        setBillingDate(parseISO(currentBillingPeriod.plannedPaymentDueDate));
        localStorage.setItem(`${accountId}-billing-date`, currentBillingPeriod.plannedPaymentDueDate);
      }
    }
  }, [accountId, currentBillingPeriod, setBillingDate, setIsBillingDateOutBound]);

  return (
    <>
      <div className="ui segment grid">
        <div className="four wide column">
          <div className="row">
            <h3>Account: {account?.accountNumber}</h3>
          </div>
          <div className="row">
            <h4 className="ui grey sub header">
              {account?.accountId}
              <CopyToClipboardIcon value={account?.accountId} />
            </h4>
          </div>          
          <div className="row ui horizontal list">
            <div className="ui grey label">Status: {account?.accountStatus?.name}</div>
            <AvailableCredit />
          </div>
        </div>
        <div className="four wide column">
          <CurrentBillingPeriod />
        </div>
        <div className="eight wide column">
          <BillingDate />
        </div>
        <div className="sixteen wide column"><EodProgress /></div>
      </div>
      <div className="ui tabular menu">
        <NavLink className="item" to={`/accounts/${accountId}/summary`}>
          Account Summary
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/authorisations`}>
          Authorisations
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/transactions`}>
          Transactions
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/billing`}>
          Billing
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/regular-payments`}>
          Regular Payment (Direct Debit)
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/bacs`}>
          Regular Payment (BACS)
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/rate-history`}>
          Interest Rates
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/memos`}>
          Memos
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/fraud`}>
          Fraud
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/account-state`}>
          Account State
        </NavLink>
        <NavLink className="item" to={`/accounts/${accountId}/pay-down-plan`}>
          Pay Down Plan
        </NavLink>
      </div>
      {<Error error={error} />}
      <Segment style={{ marginTop: '-14px', borderTop: 'none' }}>
        <Outlet />
      </Segment>
    </>
  );
}
